/* eslint react-hooks/exhaustive-deps: "warn" */
import { type MouseEvent, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmLinkage.module.scss';

export const MdmLinkage = () => {
  const { t } = useInt();
  const cardRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(cardRef, { threshold: 0.2 });

  const handleSpotlight = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <Section background="#DCE2F5" className={styles.root}>
      <div className={styles.grid}>
        <div className={cn(styles.copy, hasIntersected && styles.copyIn)}>
          <TitleBlock
            title={t('Dedupe solves the row. Linkage solves the roll-up.')}
            description={t(
              "Collapsing four records into one customer is only half the job. If the golden record has no parent, finance still can't see that six separate vendors belong to one group — so negotiated terms leak, concentration looks lower than it is, and credit limits are set against the subsidiary instead of the group.",
            )}
            align="left"
          />
          <p className={styles.desc}>
            {t(
              'Group structure is built from ownership filings and officer registers across jurisdictions, so parents, subsidiaries, and shared beneficial owners resolve to the same identifiers as the rest of your master data.',
            )}
          </p>
        </div>

        <div
          ref={cardRef}
          className={cn(styles.card, hasIntersected && styles.cardIn)}
          onMouseMove={handleSpotlight}
        >
          <span className={styles.spotlight} aria-hidden="true" />
          <span className={cn(styles.corner, styles.cornerTl)} aria-hidden="true" />
          <span className={cn(styles.corner, styles.cornerBr)} aria-hidden="true" />

          <div className={styles.cardBar}>
            <span className={styles.cardDot} />
            <span className={styles.cardLabel}>{t('Ownership graph')} · Revolut Ltd</span>
          </div>

          <div className={styles.cardBody}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className={styles.graph}
              src="/storage/img/master-data-management/ownership-graph.jpeg"
              alt="Ownership graph: Revolut Ltd with its group of subsidiaries across jurisdictions, each linked by an Owns relationship"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </Section>
  );
};
